// Production performance, derived from batches (startedAt, completedAt, dueDate, owner, stageTimes).
// Operational trends only: nothing here ranks or orders team members.
import { PERFORMANCE_RANGES, DEFAULT_RANGE, PIC_KEYS, stageTarget } from './constants.js';
import { addDays, addMonths, diffDays, startOfWeek, startOfMonth, endOfMonth, formatDate, isoDateOfTimestamp, todayISO } from './dates.js';

const DAY_MS = 86400000;
const DAYS_PER_MONTH = 365.25 / 12;
const WEEKS = { '3m': 13, '6m': 26 };
// Trailing window (days) behind every point of the score series, so the line is smooth instead of jumping per bucket.
export const SCORE_WINDOW = { day: 14, week: 28, month: 61 };
const LEGACY_RANGES = { '90d': '3m', '12m': '1y', all: '1y' };
const ACTIVE = new Set(['In Progress', 'Review', 'Blocked']);

const round1 = (x) => Math.round(x * 10) / 10;
const pct = (n, d) => (d ? round1((n / d) * 100) : null);
const avg = (xs) => (xs.length ? round1(xs.reduce((s, x) => s + x, 0) / xs.length) : null);
const between = (d, from, to) => !!d && d >= from && d <= to;
const minDate = (a, b) => (a < b ? a : b);

// Accepts the v2 keys; old links ('90d', '12m', 'all') map to the nearest new range; anything else → 30d.
export function normalizeRange(range) {
  const key = LEGACY_RANGES[range] || range;
  return PERFORMANCE_RANGES.some((r) => r.key === key) ? key : DEFAULT_RANGE;
}

// Score = mean of the on-time rate and the completion rate (0–100, 1 decimal); one missing → the other; none → null.
export function scoreOf(onTimeRate, completionRate) {
  const xs = [onTimeRate, completionRate].filter((x) => x != null);
  return xs.length ? round1(xs.reduce((s, x) => s + x, 0) / xs.length) : null;
}

// A person's batches: the ones they own, and the ones they were PIC of a started stage in (DESIGN §9 — editors and
// videographers do most of the work; ROLES-07). 'started' entries name the PICs of that moment; batches without them
// (older data, not started yet) fall back to the client's current PICs.
function involvedIn(state, index) {
  const pics = new Map();
  for (const a of state.activity || []) {
    if (a.type !== 'started' || typeof a.detail !== 'string' || !a.detail.startsWith('pic:')) continue;
    if (!pics.has(a.batchId)) pics.set(a.batchId, new Set());
    for (const id of a.detail.slice(4).split(',')) if (id) pics.get(a.batchId).add(id);
  }
  return (b, id) => {
    if (b.owner === id) return true;
    if (pics.has(b.id)) return pics.get(b.id).has(id);
    const pic = index.clientsById?.get(b.clientId)?.pic || {};
    return PIC_KEYS.some((k) => pic[k] === id);
  };
}

function scopeBatches(state, index, { clientId, monthNumber, teamMemberId }) {
  const month = Number(monthNumber) || 0;
  const involved = teamMemberId ? involvedIn(state, index) : null;
  return state.batches.filter((b) => (!clientId || b.clientId === clientId)
    && (!teamMemberId || involved(b, teamMemberId))
    && (!month || index.monthsById.get(b.monthId)?.monthNumber === month));
}

// Per-batch facts in local calendar dates. reviews: time from the (last) review request to Done, per stage.
// off: the batch's month is marked Off — it is never late and never counts as due (DESIGN §7.5).
// videos: videos delivered (the Result count) once the batch is Done.
function toFacts(batch, off = false) {
  const completed = batch.status === 'Done' && batch.completedAt ? isoDateOfTimestamp(batch.completedAt) : '';
  const reviews = [];
  for (const t of Object.values(batch.stageTimes || {})) {
    if (!t?.reviewAt || !t.doneAt) continue;
    const days = (Date.parse(t.doneAt) - Date.parse(t.reviewAt)) / DAY_MS;
    if (days >= 0) reviews.push({ done: isoDateOfTimestamp(t.doneAt), days });
  }
  return {
    batch,
    started: batch.startedAt ? isoDateOfTimestamp(batch.startedAt) : '',
    completed,
    onTime: completed ? off || !batch.dueDate || completed <= batch.dueDate : null,
    off,
    videos: completed ? Math.max(0, Number(batch.progress?.result ?? stageTarget(batch, 'result')) || 0) : 0,
    days: completed && batch.startedAt ? (Date.parse(batch.completedAt) - Date.parse(batch.startedAt)) / DAY_MS : null,
    reviews,
  };
}

// Chart buckets. Every bucket ends no later than today, and the first bucket starts on the period start, so the
// bucket sums equal the period KPIs (7d/30d: days; 3m/6m: Monday weeks; 1y: calendar months).
function bucketFrames(range, today) {
  const grain = PERFORMANCE_RANGES.find((r) => r.key === range).bucket;
  const frames = [];
  if (grain === 'day') {
    const n = Number.parseInt(range, 10);
    for (let i = n - 1; i >= 0; i--) {
      const d = addDays(today, -i);
      frames.push({ key: d, label: formatDate(d), start: d, end: d });
    }
  } else if (grain === 'week') {
    for (let s = addDays(startOfWeek(today), -7 * (WEEKS[range] - 1)); s <= today; s = addDays(s, 7)) {
      frames.push({ key: s, label: formatDate(s), start: s, end: minDate(addDays(s, 6), today) });
    }
  } else {
    for (let s = startOfMonth(addMonths(today, -11)); s <= today; s = addMonths(s, 1)) {
      frames.push({ key: s.slice(0, 7), label: `${formatDate(s).split(' ')[0]} ${s.slice(0, 4)}`, start: s, end: minDate(endOfMonth(s), today) });
    }
  }
  return { grain, frames };
}

function workloadAt(facts, date) {
  return facts.filter((f) => f.started && f.started <= date && (!f.completed || f.completed > date)).length;
}

// Completion rate: of the batches due in [from, to], the share completed by `to`. An open batch due `today` still has
// the day left: it is pending, not missed (LOGIC-06).
function periodStats(facts, from, to, today = '') {
  const done = facts.filter((f) => between(f.completed, from, to));
  const due = facts.filter((f) => !f.off && between(f.batch.dueDate, from, to)
    && !(f.batch.dueDate === today && !(f.completed && f.completed <= to)));
  const completionRate = pct(due.filter((f) => f.completed && f.completed <= to).length, due.length);
  const onTimeRate = pct(done.filter((f) => f.onTime).length, done.length);
  const reviews = facts.flatMap((f) => f.reviews).filter((r) => between(r.done, from, to));
  return {
    output: done.length,
    videos: done.reduce((n, f) => n + f.videos, 0),
    due: due.length,
    completionRate,
    onTimeRate,
    score: scoreOf(onTimeRate, completionRate),
    completionTime: avg(done.map((f) => f.days).filter((d) => d != null)),
    reviewSpeed: avg(reviews.map((r) => r.days)),
    reviews: reviews.length,
  };
}

// → { range, bucket: 'day'|'week'|'month', period: { start, end, days, previousStart, previousEnd },
//     buckets: [{ key, label, start, end, output, videos, completionRate, onTimeRate, score, activeWorkload }],
//     series: [{ key, label, value /* score over the trailing SCORE_WINDOW[bucket] days, or null */ }], scoreWindow,
//     kpis: { score, output, videos, velocity, onTimeRate, completionRate, completionTime, activeWorkload, reviewSpeed }, summary }
export function computePerformance(state, index, { range = DEFAULT_RANGE, clientId = '', monthNumber = 0, teamMemberId = '', today } = {}) {
  range = normalizeRange(range);
  today = today || todayISO();
  const scope = scopeBatches(state, index, { clientId, monthNumber, teamMemberId });
  const facts = scope.map((b) => toFacts(b, !!index.monthsById.get(b.monthId)?.off));
  const { grain, frames } = bucketFrames(range, today);
  const window = SCORE_WINDOW[grain];

  const buckets = frames.map((fr) => {
    const s = periodStats(facts, fr.start, fr.end, today);
    return {
      ...fr,
      output: s.output,
      videos: s.videos,
      completionRate: s.completionRate,
      onTimeRate: s.onTimeRate,
      score: periodStats(facts, addDays(fr.end, -(window - 1)), fr.end, today).score,
      activeWorkload: workloadAt(facts, fr.end),
    };
  });

  const start = frames[0].start;
  const days = diffDays(start, today) + 1;
  const prevEnd = addDays(start, -1);
  const prevStart = addDays(start, -days);
  const cur = periodStats(facts, start, today, today);
  const prev = periodStats(facts, prevStart, prevEnd);

  const perMonth = grain === 'month';
  // A partial first unit counts as one whole unit, so a short period never extrapolates beyond its real output.
  const units = Math.max(1, days / (perMonth ? DAYS_PER_MONTH : 7));
  const change = (a, b) => (a == null || !b ? null : round1(((a - b) / b) * 100));
  const diff = (a, b) => (a == null || b == null ? null : round1(a - b));

  const active = scope.filter((b) => ACTIVE.has(b.status)).length;
  const allDone = facts.filter((f) => f.completed);
  const count = (status) => scope.filter((b) => b.status === status).length;

  return {
    range,
    bucket: grain,
    period: { start, end: today, days, previousStart: prevStart, previousEnd: prevEnd },
    buckets,
    series: buckets.map((b) => ({ key: b.key, label: b.label, value: b.score })),
    scoreWindow: window,
    kpis: {
      // 0–100; delta in points.
      score: { value: cur.score, previous: prev.score, delta: diff(cur.score, prev.score) },
      output: { value: cur.output, previous: prev.output, delta: change(cur.output, prev.output) },
      // Videos delivered: the Result counts of batches completed in the period; delta in %.
      videos: { value: cur.videos, previous: prev.videos, delta: change(cur.videos, prev.videos) },
      velocity: { value: round1(cur.output / units), unit: perMonth ? 'per month' : 'per week', delta: change(cur.output, prev.output) },
      // Rates in %, deltas in percentage points.
      onTimeRate: { value: cur.onTimeRate, previous: prev.onTimeRate, delta: diff(cur.onTimeRate, prev.onTimeRate) },
      completionRate: { value: cur.completionRate, due: cur.due, delta: diff(cur.completionRate, prev.completionRate) },
      completionTime: { value: cur.completionTime, delta: diff(cur.completionTime, prev.completionTime) },
      activeWorkload: { value: active, delta: active - workloadAt(facts, prevEnd) },
      // Average days a stage spent in Review (review request → Done) for stages finished in the period. Lower is better;
      // delta in % (negative = faster).
      reviewSpeed: { value: cur.reviewSpeed, previous: prev.reviewSpeed, delta: change(cur.reviewSpeed, prev.reviewSpeed), count: cur.reviews, better: 'lower' },
    },
    summary: {
      totalBatches: scope.length,
      completed: count('Done'),
      active,
      inProgress: count('In Progress'),
      review: count('Review'),
      blocked: count('Blocked'),
      toDo: count('To Do'),
      delayed: facts.filter((f) => !f.off && f.batch.status !== 'Done' && f.batch.dueDate && f.batch.dueDate < today).length,
      videos: allDone.reduce((n, f) => n + f.videos, 0),
      onTimeRate: pct(allDone.filter((f) => f.onTime).length, allDone.length),
      avgCompletionDays: avg(allDone.map((f) => f.days).filter((d) => d != null)),
      currentWorkload: active,
    },
  };
}
